import { useEffect, useMemo, useState } from 'react';
import { Zap, ShieldAlert, Plus, Trash2 } from '../../lib/icons';
import { toast } from 'sonner';
import { fetchCuves, retourAtelier, estBlocageFiliere, tauxRetenue, repartirProrata } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';
import { estAdmin } from '../../lib/permissions';

// Saisie rapide depuis le Planning : un total d'huile MESURÉ pour les lots réellement
// triturés ensemble, ventilé sur les cuves, sans changer de page.
// Le groupe est explicite (cases à cocher) : on ne suppose jamais que tous les lots d'un
// client sont passés ensemble, et un total n'est jamais réparti entre clients différents
// (règle partagée avec le Retour d'atelier via `repartirProrata`).
export const SaisieRapideOf = ({ of, onDone }) => {
  const { user } = useAuth();
  const [cuves, setCuves] = useState([]);
  const [lignes, setLignes] = useState([{ cuveId: '', kg: '' }]);
  const [huileDirecte, setHuileDirecte] = useState('');
  const [enCours, setEnCours] = useState(false);

  const restants = useMemo(
    () => of.lignes.filter((l) => l.statut !== 'Trituré' && l.statut !== 'Décompté'),
    [of.lignes]
  );
  const cleTiers = (l) => (l.parcours === 'client' ? `client:${l.tiersId ?? l.tiers}` : 'fournisseurs');
  // Un seul lot restant : le groupe est sans ambiguïté. Sinon, l'opérateur coche.
  const [groupe, setGroupe] = useState(() => (restants.length === 1 ? [restants[0].id] : []));
  const lotsGroupe = restants.filter((l) => groupe.includes(l.id));
  const famille = of.famille === 'lampante' ? 'lampante' : 'noble';
  // Sortie immédiate stricte : tous les lots du groupe sont des clients qui repartent avec l'huile.
  const sortieImmediate = lotsGroupe.length > 0 && lotsGroupe.every((l) => l.parcours === 'client' && l.devenir === 'sortie');
  const besoinCuve = !sortieImmediate;
  const olives = lotsGroupe.reduce((s, l) => s + l.poidsNet, 0);
  const groupesDispo = [...new Set(restants.map(cleTiers))].length;

  useEffect(() => {
    if (besoinCuve) fetchCuves().then(setCuves);
  }, [besoinCuve]);

  const basculer = (lot) => {
    if (groupe.includes(lot.id)) return setGroupe((g) => g.filter((x) => x !== lot.id));
    if (lotsGroupe.length && cleTiers(lotsGroupe[0]) !== cleTiers(lot))
      return toast.error(
        'Ces lots appartiennent à des passages différents : un total d’huile mesuré ne se partage jamais entre deux clients.'
      );
    setGroupe((g) => [...g, lot.id]);
  };
  const toutCePassage = (lot) =>
    setGroupe(restants.filter((l) => cleTiers(l) === cleTiers(lot)).map((l) => l.id));

  const cuvesOk = useMemo(
    () => cuves.filter((c) => !c.virtuelle && c.id !== 'CUVE_SORTIE_DIRECTE' && !c.verrouillee),
    [cuves]
  );

  const maj = (i, k, v) => setLignes((ls) => ls.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const ajouter = () => setLignes((ls) => [...ls, { cuveId: '', kg: '' }]);
  const retirer = (i) => setLignes((ls) => (ls.length === 1 ? ls : ls.filter((_, j) => j !== i)));

  // Cumul par cuve : une cuve utilisée sur deux lignes est contrôlée sur le total.
  const detail = lignes.map((l) => ({ ...l, cuve: cuvesOk.find((c) => c.id === l.cuveId), kgNum: Number(l.kg) || 0 }));
  const cumulParCuve = detail.reduce((m, l) => (l.cuve ? { ...m, [l.cuve.id]: (m[l.cuve.id] || 0) + l.kgNum } : m), {});
  const croisees = detail.filter((l) => l.cuve && (l.cuve.famille ?? 'noble') !== famille);
  const saturees = Object.entries(cumulParCuve)
    .map(([id, kg]) => ({ cuve: cuvesOk.find((c) => c.id === id), kg }))
    .filter((x) => x.cuve && x.kg > x.cuve.capacite - x.cuve.volume);
  const doublons = detail.some((l, i) => l.cuve && detail.findIndex((x) => x.cuveId === l.cuveId) !== i);
  const q = besoinCuve ? Number(detail.reduce((s, l) => s + l.kgNum, 0).toFixed(3)) : Number(huileDirecte) || 0;
  // Aperçu de la répartition : mêmes arrondis que l'enregistrement.
  const apercu = lotsGroupe.length && q > 0 ? repartirProrata(q, lotsGroupe) : null;
  // Retenue d'huile : uniquement sur les prestations clients dont l'huile est versée en cuve.
  const TAUX = tauxRetenue();
  const netRetenu = besoinCuve ? lotsGroupe.filter((l) => l.parcours === 'client').reduce((s, l) => s + l.poidsNet, 0) : 0;
  const retenuePrevue = {
    net: netRetenu,
    kg: Number((q * (olives > 0 ? netRetenu / olives : 0) * TAUX).toFixed(3)),
  };

  const valider = async (override = false) => {
    const { error: errRep, parts } = repartirProrata(q, lotsGroupe);
    if (errRep) return toast.error(errRep);
    setEnCours(true);
    const cuvesLignes = detail.filter((l) => l.cuve && l.kgNum > 0).map((l) => ({ cuveId: l.cuveId, kg: l.kgNum }));
    // Chaque lot du groupe reçoit sa part, ventilée sur les mêmes cuves au même ratio.
    const payload = parts.map(({ id, huile }) => {
      const ratio = q > 0 ? huile / q : 0;
      const rep = cuvesLignes.map((p, j) =>
        j === cuvesLignes.length - 1
          ? {
              cuveId: p.cuveId,
              kg: Number(
                (huile - cuvesLignes.slice(0, -1).reduce((s, x) => s + Number((x.kg * ratio).toFixed(3)), 0)).toFixed(3)
              ),
            }
          : { cuveId: p.cuveId, kg: Number((p.kg * ratio).toFixed(3)) }
      );
      return { id, huile, repartition: rep };
    });
    const { error, termine } = await retourAtelier(of.id, payload, user?.name, {
      override,
      // Clôture seulement si ce passage couvre tous les lots restants de l'OF.
      cloturer: lotsGroupe.length === restants.length,
    });
    setEnCours(false);
    if (error) {
      if (
        !override &&
        estBlocageFiliere(error) &&
        estAdmin(user?.role) &&
        window.confirm(`${error}\n\nOVERRIDE ADMINISTRATEUR : forcer quand même ? L'opération sera tracée dans le journal d'audit.`)
      )
        return valider(true);
      return toast.error(error);
    }
    toast.success(
      termine
        ? `OF ${of.numero} clôturé — ${fmt(q, 3)} kg d'huile${besoinCuve ? ` ventilés sur ${cuvesLignes.length} cuve(s)` : ' remis au client'}`
        : `OF ${of.numero} mis à jour — ${fmt(q, 3)} kg répartis sur ${parts.length} lot(s)`
    );
    setLignes([{ cuveId: '', kg: '' }]);
    setHuileDirecte('');
    setGroupe([]);
    onDone?.();
  };

  if (restants.length === 0) return null;

  const bloque =
    enCours ||
    q <= 0 ||
    lotsGroupe.length === 0 ||
    Boolean(apercu?.error) ||
    (besoinCuve &&
      (detail.filter((l) => l.cuve && l.kgNum > 0).length === 0 ||
        detail.some((l) => (l.cuveId && l.kgNum <= 0) || (!l.cuveId && l.kgNum > 0)) ||
        doublons ||
        saturees.length > 0 ||
        (croisees.length > 0 && !estAdmin(user?.role))));

  return (
    <div data-testid={`saisie-rapide-${of.id}`} className="border-t border-dashed border-slate-300 bg-slate-50 px-4 py-3">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 font-heading text-xs font-bold uppercase tracking-[0.08em] text-slate-700">
          <Zap size={14} className="text-amber-600" /> Saisie rapide — total mesuré & ventilation multi-cuves
        </p>
        {besoinCuve && (
          <button
            type="button"
            data-testid={`rapide-ajouter-cuve-${of.id}`}
            onClick={ajouter}
            className="flex h-9 items-center gap-1.5 border border-slate-900 bg-white px-2.5 text-xs font-bold uppercase tracking-[0.06em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <Plus size={13} /> Ajouter une cuve
          </button>
        )}
      </div>

      <div data-testid={`rapide-groupe-${of.id}`} className="mt-2 border border-slate-300 bg-white">
        <p className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 font-data text-[11px] font-bold uppercase tracking-[0.08em] text-slate-600">
          Lots réellement triturés ensemble — à cocher
          {groupesDispo > 1 && (
            <span className="ml-2 border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-800">
              {groupesDispo} passages distincts dans cet OF
            </span>
          )}
        </p>
        <div className="divide-y divide-slate-100">
          {restants.map((l) => (
            <label
              key={l.id}
              data-testid={`rapide-lot-${of.id}-${l.id}`}
              className={`flex cursor-pointer flex-wrap items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 ${
                groupe.includes(l.id) ? 'bg-slate-100' : ''
              }`}
            >
              <input
                type="checkbox"
                data-testid={`rapide-coche-${of.id}-${l.id}`}
                checked={groupe.includes(l.id)}
                onChange={() => basculer(l)}
                className="h-4 w-4 accent-slate-900"
              />
              <span className="font-data font-semibold text-slate-900">{l.id}</span>
              <span className="text-slate-700">{l.tiers}</span>
              <span className="font-data text-[11px] text-slate-500">
                {l.qualite} · {fmt(l.poidsNet)} kg d'olives{l.devenir === 'sortie' ? ' · sortie immédiate' : ''}
              </span>
              {restants.filter((x) => cleTiers(x) === cleTiers(l)).length > 1 && (
                <button
                  type="button"
                  data-testid={`rapide-tout-passage-${of.id}-${l.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    toutCePassage(l);
                  }}
                  className="ml-auto border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold text-sky-700 hover:bg-sky-100"
                >
                  tout ce passage
                </button>
              )}
            </label>
          ))}
        </div>
      </div>

      {besoinCuve ? (
        <div className="mt-2 space-y-2">
          {lignes.map((l, i) => {
            const c = cuvesOk.find((x) => x.id === l.cuveId);
            return (
              <div key={i} data-testid={`rapide-ligne-${of.id}-${i}`} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  data-testid={`rapide-kg-${of.id}-${i}`}
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder="kg"
                  value={l.kg}
                  onChange={(e) => maj(i, 'kg', e.target.value)}
                  className="tabular h-11 w-28 border border-slate-300 bg-white px-2 text-center font-data text-sm outline-none focus:border-slate-900"
                />
                <select
                  data-testid={`rapide-cuve-${of.id}-${i}`}
                  value={l.cuveId}
                  onChange={(e) => maj(i, 'cuveId', e.target.value)}
                  className="h-11 flex-1 border border-slate-300 bg-white px-2 font-data text-sm outline-none focus:border-slate-900"
                >
                  <option value="">— Cuve de destination (filière {famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'}) —</option>
                  {cuvesOk.map((x) => (
                    <option key={x.id} value={x.id}>
                      {`${x.nom} · ${(x.famille ?? 'noble') === 'lampante' ? 'LAMPANTE' : 'NOBLE'} · reste ${Math.round(x.capacite - x.volume).toLocaleString('fr-FR')} ${x.unite}`}
                    </option>
                  ))}
                </select>
                {c && (
                  <span className="font-data text-[11px] text-slate-500 sm:w-36">
                    après : {fmt(c.volume + (Number(l.kg) || 0))} / {fmt(c.capacite)} {c.unite}
                  </span>
                )}
                <button
                  type="button"
                  data-testid={`rapide-retirer-${of.id}-${i}`}
                  onClick={() => retirer(i)}
                  className="h-11 border border-slate-300 bg-white px-2 text-slate-600 transition-colors duration-150 hover:bg-red-600 hover:text-white"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            data-testid={`rapide-huile-${of.id}`}
            type="number"
            step="0.001"
            min="0"
            placeholder="kg"
            value={huileDirecte}
            onChange={(e) => setHuileDirecte(e.target.value)}
            className="tabular h-11 w-28 border border-slate-300 bg-white px-2 text-center font-data text-sm outline-none focus:border-slate-900"
          />
          <p data-testid={`rapide-sortie-immediate-${of.id}`} className="flex-1 border border-slate-300 bg-white px-3 py-2.5 font-data text-[11px] text-slate-600">
            SORTIE IMMÉDIATE — le client repart avec son huile : aucune cuve de l'huilerie concernée.
            Emballages facultatifs : sans saisie, aucun contenant n'est facturé au client.
          </p>
        </div>
      )}

      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p data-testid={`rapide-total-${of.id}`} className="font-data text-[11px] text-slate-600">
          Total huile mesuré : <span className="font-semibold text-slate-900">{fmt(q, 3)} kg</span> ·{' '}
          {lotsGroupe.length} lot(s) coché(s) sur {restants.length} restant(s) · {fmt(olives)} kg d'olives
          {q > 0 && olives > 0 ? ` · rendement du groupe ${fmt((q / olives) * 100, 2)} %` : ''} — réparti au prorata du
          poids net de chaque lot coché.
        </p>
        <button
          type="button"
          data-testid={`rapide-valider-${of.id}`}
          disabled={bloque}
          onClick={() => valider(false)}
          className={`h-11 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 ${
            bloque ? 'cursor-not-allowed bg-slate-300' : 'bg-emerald-700 hover:bg-emerald-800'
          }`}
        >
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {apercu?.error && (
        <p data-testid={`rapide-repartition-refus-${of.id}`} className="mt-2 border-2 border-red-600 bg-red-50 px-3 py-2 text-xs font-bold text-red-800">
          {apercu.error}
        </p>
      )}
      {apercu?.parts && lotsGroupe.length > 1 && (
        <p data-testid={`rapide-repartition-${of.id}`} className="mt-2 border-l-4 border-sky-600 bg-sky-50 px-3 py-2 font-data text-[11px] text-sky-900">
          Répartition :{' '}
          {apercu.parts.map((p) => `${p.id} → ${fmt(p.huile, 3)} kg`).join(' · ')} — somme{' '}
          {fmt(apercu.parts.reduce((s, p) => s + p.huile, 0), 3)} kg = total saisi.
        </p>
      )}

      {besoinCuve && retenuePrevue.net > 0 && (
        <p data-testid={`rapide-retenue-${of.id}`} className="mt-2 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 font-data text-[11px] text-amber-900">
          Retenue d'huile {(TAUX * 100).toFixed(2).replace('.', ',')} % appliquée automatiquement sur les prestations
          stockées à l'huilerie ({fmt(retenuePrevue.net)} kg d'olives) : <span className="font-bold">{fmt(retenuePrevue.kg, 3)} kg</span> déduits
          du stock client et basculés au stock financier de l'huilerie, valorisés au CMP de la cuve.
        </p>
      )}

      {doublons && (
        <p data-testid={`rapide-doublon-${of.id}`} className="mt-2 border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900">
          Une même cuve est sélectionnée sur plusieurs lignes : regroupez la quantité sur une seule ligne.
        </p>
      )}
      {croisees.map((l) => (
        <p key={l.cuve.id} data-testid={`rapide-blocage-${of.id}`} className="mt-2 flex items-start gap-2 border-2 border-red-600 bg-red-50 px-3 py-2 text-xs font-bold text-red-800">
          <ShieldAlert size={15} className="mt-0.5 flex-shrink-0" />
          BLOCAGE SÉCURITÉ — un lot {famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'} ne peut pas être versé dans la cuve « {l.cuve.nom} ».
        </p>
      ))}
      {saturees.map((s) => (
        <p key={s.cuve.id} data-testid={`rapide-capacite-${of.id}`} className="mt-2 border-2 border-red-600 bg-red-50 px-3 py-2 text-xs font-bold text-red-800">
          Capacité insuffisante dans « {s.cuve.nom} » : {fmt(s.kg, 1)} kg demandés, reste {fmt(s.cuve.capacite - s.cuve.volume)} {s.cuve.unite}.
        </p>
      ))}
    </div>
  );
};
